import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/users.js` GET /users, which responds
// `{ users, pagination, config, filtersConfig }`. Only `users` is consumed here, and each user row
// carries more than an LOV needs (mail, public_key, user_settings, …). The projection narrows to the
// identifiers an LOV row renders.

export interface UserSummary {
  readonly user_id: number;
  readonly username: string;
}

const summarySchema = object<UserSummary>({
  user_id: num(),
  username: str(),
});

interface UsersResponse {
  readonly users: readonly UserSummary[];
}

const responseSchema = object<UsersResponse>({
  users: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Fetch all users the current user can see, for a picker. No pagination — the LOV needs the complete
 * set so Typeahead's client-side filter has every option to match against. If the universe of users
 * ever grows past a UX-bearable dropdown size, this is the call site to migrate to server-side search.
 */
export const getUsersLOV = async (): Promise<readonly UserSummary[]> => {
  const res = await api.get('/users', {
    params: { limit: LOV_LIMIT },
  });
  const parsed = runSchema(responseSchema, res.data, 'getUsersLOV');
  if (!parsed.ok) {
    throw new Error('getUsersLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['users']).users;
};
